import { useMutation, useQueryClient } from "@tanstack/react-query";
import { authService } from "../services/auth.service";
import { useAuth } from "../context/AuthContext";

/**
 * useAuthMutations()
 *
 * React Query layer over auth.service.js — same pattern as useRecipients().
 * AuthContext (useAuth) only ever holds state; this hook is what actually
 * calls the API and, on success, pushes the result into that state via
 * setUser/clearUser.
 *
 * Usage:
 *   const { signup, login, logout, logoutAll, changePassword,
 *           isSigningUp, isLoggingIn, signupError, loginError } = useAuthMutations();
 *
 *   login({ identifier, password }, { onSuccess: () => navigate("/home") });
 */
export function useAuthMutations() {
  const { setUser, clearUser } = useAuth();
  const queryClient = useQueryClient();

  const signupMutation = useMutation({
    mutationFn: (payload) => authService.signup(payload),
  });

  const verifyEmailMutation = useMutation({
    mutationFn: (payload) => authService.verifyEmail(payload),
    onSuccess: (data) => setUser(data.user),
  });

  const resendEmailOtpMutation = useMutation({
    mutationFn: (verificationId) => authService.resendEmailOtp(verificationId),
  });

  const loginMutation = useMutation({
    mutationFn: (payload) => authService.login(payload), // { identifier, password }
    onSuccess: (data) => setUser(data.user),
  });

  const logoutMutation = useMutation({
    mutationFn: () => authService.logout(), // revokes server-side, clears tokens internally
    onSuccess: () => {
      clearUser();
      // Drop any cached server data tied to the now-logged-out user
      // (recipients, etc.) so the next login doesn't briefly show stale data.
      queryClient.clear();
    },
  });

  const logoutAllMutation = useMutation({
    mutationFn: () => authService.logoutAll(),
    onSuccess: () => {
      clearUser();
      queryClient.clear();
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: ({ currentPassword, newPassword }) =>
      authService.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    onSuccess: () => {
      // Backend revokes all refresh sessions on password change — local
      // tokens are now dead, so force a logout state regardless of the
      // fact the call itself "succeeded".
      clearUser();
      queryClient.clear();
    },
  });

  return {
    signup: signupMutation.mutate,
    signupAsync: signupMutation.mutateAsync,
    isSigningUp: signupMutation.isPending,
    signupError: signupMutation.error,

    verifyEmailAsync: verifyEmailMutation.mutateAsync,
    isVerifyingEmail: verifyEmailMutation.isPending,
    verifyEmailError: verifyEmailMutation.error,

    resendEmailOtpAsync: resendEmailOtpMutation.mutateAsync,
    isResendingEmailOtp: resendEmailOtpMutation.isPending,
    resendEmailOtpError: resendEmailOtpMutation.error,

    login: loginMutation.mutate,
    loginAsync: loginMutation.mutateAsync,
    isLoggingIn: loginMutation.isPending,
    loginError: loginMutation.error,

    logout: logoutMutation.mutate,
    logoutAsync: logoutMutation.mutateAsync,
    isLoggingOut: logoutMutation.isPending,

    logoutAll: logoutAllMutation.mutate,
    logoutAllAsync: logoutAllMutation.mutateAsync,
    isLoggingOutAll: logoutAllMutation.isPending,

    changePassword: changePasswordMutation.mutate,
    changePasswordAsync: changePasswordMutation.mutateAsync,
    isChangingPassword: changePasswordMutation.isPending,
    changePasswordError: changePasswordMutation.error,
  };
}
